import type { MeasurementReviewField } from "./measurement-review";

const normal = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const size = String.raw`(\d+(?:\.\d+)?)\s*(mm|m)\b`;
const metres = (value: string, unit: string) => Number(value) / (unit.toLowerCase() === "mm" ? 1000 : 1);
const identity = (label: string) => {
  let hash = 0;
  for (const c of normal(label)) hash = (Math.imul(hash, 31) + c.charCodeAt(0)) | 0;
  return `description-size-${(hash >>> 0).toString(36)}`;
};

/** Copy explicit principal sizes from prose locally. No drawing read or AI call.
 * Member lengths remain member lengths, not inferred clear opening widths.
 */
export function descriptionReviewMeasurements(description: string): MeasurementReviewField[] {
  if (!/\bextension\b/i.test(description)) return [];
  const extensionName = /\brear extension\b/i.test(description) ? "Rear extension" : "Extension";
  const fields = new Map<string, MeasurementReviewField>();
  const add = (label: string, value: number, line: string) => {
    const id = identity(label);
    fields.set(id, { id, label, unit: "m", value: String(value), note: line.slice(0, 1000),
      descriptionBasis: /\bassum(?:ed|ption)\b/i.test(line) ? "assumed" : "stated" });
  };
  const wallRuns = new Map<string, [number, string]>();
  const foundationRuns = new Map<string, [number, string]>();
  for (const raw of description.split(/\r?\n/)) {
    const line = raw.replace(/^\s*\d+(?:\.\d+)*[.)]?\s+/, "").trim();
    if (/\b(?:no|without|excluding)\s+(?:new\s+)?(?:external\s+walls?|steel|beams?|extension)\b/i.test(line)) continue;
    if (/\bextension footprint\b/i.test(line)) {
      const pair = line.match(new RegExp(`${size}\\s*(?:by|×|x)\\s*${size}`, "i"));
      if (pair) {
        add(`${extensionName} width`, metres(pair[1], pair[2]), line);
        add(`${extensionName} length`, metres(pair[3], pair[4]), line);
      }
    }
    // Wall runs are copied from whichever wording the description uses.
    // Foundation runs follow the same outline and are only a fallback.
    if (/\b(?:rear|left|right)\b/i.test(line) && !/\b(?:demolish|remove|parapets?|internal|partition)\b/i.test(line) &&
      (/\bextension\b|\bexternal\b|\bcavity\b|\bmasonry\b|\bfoundation|\btrench|\bfooting/i.test(line))) {
      const foundation = /\b(?:foundations?|footings?|trenches?|excavat\w*)\b/i.test(line);
      for (const match of line.matchAll(new RegExp(`\\b(rear|left(?:[- ]hand)?|right(?:[- ]hand)?)\\s+(?:side\\s+|flank\\s+|elevation\\s+)?(?:external\\s+)?(wall|return|run)\\b([^\\d;,]{0,45}?)${size}`, "gi"))) {
        if (/\b(?:nib|opening|window|door|height|lintel|beam)\b/i.test(match[3])) continue;
        if (/^rear$/i.test(match[1]) && /return/i.test(match[2])) continue;
        const location = /left/i.test(match[1]) ? "Left return" : /right/i.test(match[1]) ? "Right return" : "Rear wall";
        const target = foundation ? foundationRuns : wallRuns;
        if (!target.has(location)) target.set(location, [metres(match[4], match[5]), line]);
      }
    }
    if (/\b(?:external wall|extension wall)s?\s+(?:overall\s+)?height\b/i.test(line)) {
      const height = line.match(new RegExp(`\\b(?:external wall|extension wall)s?\\s+(?:overall\\s+)?height\\s*(?:is\\s*|of\\s*|[:=–—]\\s*)?${size}`, "i"));
      if (height) add("External wall height", metres(height[1], height[2]), line);
    }
    if (/\b(?:install|provide)\b.*\b(?:steel )?beam\b.*\bopening\b/i.test(line)) {
      const length = line.match(new RegExp(`\\b(?:cut\\s+|beam\\s+)?length\\s*(?:is\\s*|of\\s*|[:=–—]\\s*)?${size}`, "i"));
      const location = line.match(/\bopening\s+(.+?)(?:[;.]|$)/i)?.[1]?.trim();
      if (length && location) add(`Beam over opening ${location.slice(0, 90)} length`,
        metres(length[1], length[2]), line);
    }
  }
  const runs = wallRuns.size ? wallRuns : foundationRuns;
  const runFields = new Map<string, MeasurementReviewField>();
  for (const location of ["Rear wall", "Left return", "Right return"]) {
    const run = runs.get(location);
    if (!run) continue;
    const label = `${location} length`;
    runFields.set(identity(label), { id: identity(label), label, unit: "m", value: String(run[0]),
      note: run[1].slice(0, 1000), descriptionBasis: /\bassum(?:ed|ption)\b/i.test(run[1]) ? "assumed" : "stated" });
  }
  return [...runFields.values(), ...fields.values()];
}

/** Keep user changes and source bindings; description alone isn't drawing evidence. */
export function includeDescriptionMeasurements(fields: MeasurementReviewField[], description: string) {
  const result = [...fields];
  for (const incoming of descriptionReviewMeasurements(description)) {
    const index = result.findIndex(f => normal(f.label) === normal(incoming.label) || f.id === incoming.id ||
      /^(?:rear )?extension (?:length|width)$/i.test(incoming.label) && /\bextension\b/i.test(f.reviewLabel || f.label) &&
      !/\b(?:wall|run|return|opening|roof|room)\b/i.test(f.reviewLabel || f.label) &&
      /\b(length|width)\b/i.exec(incoming.label)?.[1]?.toLowerCase() ===
        /\b(length|width)\b/i.exec(f.reviewLabel || f.label)?.[1]?.toLowerCase());
    if (index < 0) result.push(incoming);
    else {
      const old = result[index];
      if (old.custom || old.edited || old.removed || old.openingEstimate || old.original?.source === "entered") continue;
      // Retain real evidence if it already agrees. Never replace its technical identity.
      result[index] = old.original && old.value === incoming.value ? { ...old, descriptionBasis: incoming.descriptionBasis } :
        { ...incoming, id: old.id, label: old.label, reviewLabel: old.reviewLabel,
          subjectId: old.subjectId, subjectKind: old.subjectKind, subjectLabel: old.subjectLabel };
    }
  }
  return result;
}
